import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import { useCurrency } from '@/lib/entities/currency';
import { IExtendedOwnProjects } from '@/lib/entities/page';

import { CircleButton, Button } from '@/lib/shared/components/Buttons';
import { IInfoCard, InfoCard } from '@/lib/shared/components/Cards/InfoCard';
import { modal, FeedBackModalNew } from '@/lib/shared/components/Modal';
import { H1, P2 } from '@/lib/shared/components/Typography';
import { useDebounce } from '@/lib/shared/hooks/use-debounce';
import { useInterval } from '@/lib/shared/hooks/use-interval';
import { ArrowIcon, VerticalLineSmall } from '@/lib/shared/icons';
import { staticResource } from '@/lib/shared/utils/resources';
import { toMoney } from '@/lib/shared/utils/to-money';

import { clsx } from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import styles from './TitleBlockFade.module.scss';
import { Delay, landingsKeys, largeDelay, normalDelay } from './constants';

type TitleBlockProps = {
  projects: IExtendedOwnProjects[];
  isLocalImages?: boolean;
  landingType: keyof typeof landingsKeys;
  title: string;
  description: string;
  descSpan?: string;
  infoCards?: IInfoCard[];
  hideButton?: boolean;
  label?: string;
  objectPosition?: CSSProperties['objectPosition'];
  hideObjectNamesOnMobile?: boolean;
  customWhatsappUrl?: string;
  h1?: string | null;
};

export const TitleBlockFade = ({
  projects,
  isLocalImages,
  landingType,
  title,
  description,
  descSpan,
  infoCards = [],
  hideButton,
  label,
  objectPosition,
  hideObjectNamesOnMobile,
  customWhatsappUrl,
  h1,
}: TitleBlockProps) => {
  const { t } = useTranslation('index');
  const {
    currency: { symbol: cur, value },
  } = useCurrency();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    inViewThreshold: 1,
    duration: 0.01,
    skipSnaps: true,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [delay, setDelay] = useState<Delay>(normalDelay);
  const [currentProject, setCurrentProject] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const debounceSetReady = useDebounce(() => {
    setIsReady(true);
  }, 250);

  const money = toMoney(projects[currentProject]?.price?.[value]);

  const toCallModal = () => {
    const keys = landingsKeys[landingType];
    modal.open(
      <FeedBackModalNew
        {...keys}
        eventName={['sale']}
        customWhatsappUrl={customWhatsappUrl}
      />,
      {
        isFeedBackNew: true,
      }
    );
  };

  const changeSlide = useCallback(() => {
    if (!emblaApi) return;
    const slideNum = emblaApi.selectedScrollSnap();
    setCurrentProject(() => slideNum);
  }, [emblaApi]);

  const resizeReady = useCallback(() => {
    if (emblaApi) {
      setIsReady(() => false);
      emblaApi.reInit();
    }
  }, [emblaApi]);

  const location = `${t(`locations.${projects[currentProject].region}`)}, 
  ${t(`locations.${projects[currentProject].district}`)}`;

  const scrollPrev = useCallback(
    (delay: Delay) => {
      setDelay(delay);
      if (emblaApi) {
        const index =
          currentProject === 0 ? projects.length - 1 : currentProject - 1;
        emblaApi.scrollTo(index, true);
      }
    },
    [emblaApi, currentProject, projects.length]
  );

  const scrollNext = useCallback(
    (delay: Delay) => {
      setDelay(delay);
      if (emblaApi) {
        const index =
          currentProject === projects.length - 1 ? 0 : currentProject + 1;
        emblaApi.scrollTo(index, true);
      }
    },
    [emblaApi, currentProject, projects.length]
  );

  const scrollTo = useCallback(
    (slideNum: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(slideNum, true);
      setDelay(largeDelay);
    },
    [emblaApi]
  );

  useEffect(() => {
    debounceSetReady();
  }, [debounceSetReady]);

  const resetDelay = useCallback(() => {
    setDelay(() => null);
    setTimeout(() => {
      setDelay(() => largeDelay);
    }, 0);
  }, []);

  useEffect(() => {
    if (emblaApi) {
      emblaApi
        .on('select', changeSlide)
        .on('select', resetDelay)
        .on('init', debounceSetReady)
        .on('resize', resizeReady)
        .on('reInit', debounceSetReady);
    }
    return () => {
      emblaApi
        ?.off('select', changeSlide)
        .off('init', debounceSetReady)
        .off('select', resetDelay)
        .off('resize', resizeReady)
        .off('reInit', debounceSetReady);
    };
  }, [changeSlide, emblaApi, debounceSetReady, resizeReady, resetDelay]);

  useInterval(() => scrollNext(normalDelay), delay);

  if (projects.length === 0) return null;
  return (
    <section className={clsx(styles.titleBlock, styles.container)}>
      <div className={styles.titleBlock__galleryWrapper} ref={emblaRef}>
        <div
          className={clsx(
            styles.titleBlock__gallery,
            isReady && styles['isReady']
          )}
          ref={containerRef}
        >
          {projects.map((project, i) => {
            return (
              <div
                key={project.name}
                className={clsx(
                  styles.titleBlock__slide,
                  currentProject === i && styles['titleBlock__slide--active']
                )}
              >
                <div
                  className={clsx(
                    styles.titleBlock__filter,
                    landingType === 'apartments' &&
                      styles['titleBlock__filter--light']
                  )}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: 60,
                  }}
                >
                  {landingType === 'apartments' && (
                    <div className={styles.titleBlock__filter__ellipse} />
                  )}
                  <Image
                    sizes="(max-width: 768px) 150vw, (max-width: 1320px) 100vw,(max-width: 1624px) 80vw, 1440px"
                    src={
                      isLocalImages
                        ? project.image
                        : staticResource(project.image)
                    }
                    className={styles.titleBlock__image}
                    alt="mainImage"
                    fill={true}
                    style={{
                      objectFit: 'cover',
                      objectPosition: objectPosition || 'center',
                    }}
                    priority={true}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.titleBlock__noise} />
      <div className={styles.titleBlock__info}>
        <div className={styles.titleBlock__infoLeft}>
          <H1 className={styles.titleBlock__title}>{h1 || t(title)}</H1>
          <P2 className={styles.titleBlock__description}>
            {t(description)}
            {descSpan && (
              <span className={styles.titleBlock__description_descSpan}>
                {' '}
                {t(descSpan)}
              </span>
            )}
          </P2>
          {!hideButton && (
            <Button
              className={clsx(
                styles.titleBlock__button,
                infoCards?.length > 0 && styles['titleBlock__button--cards']
              )}
              as={'button'}
              label={t(label || 'form.buttons.requestSelection')}
              theme={'light-invert'}
              size={'md'}
              onClick={toCallModal}
            />
          )}
        </div>
        <div className={styles.titleBlock__infoRight}>
          {infoCards.length > 0 && (
            <div className={styles.titleBlock__infoCards}>
              {infoCards.map((c) => {
                return <InfoCard {...c} key={c.text} />;
              })}
            </div>
          )}
          {infoCards?.length > 0 && (
            <Button
              className={styles['titleBlock__infoCardsButton']}
              as={'button'}
              label={t(label || 'form.buttons.requestSelection')}
              theme={'light-invert'}
              size={'md'}
              onClick={toCallModal}
            />
          )}
          <div className={styles.titleBlock__controls}>
            <ul className={styles.titleBlock__controlsPads}>
              {projects.map((p, i) => {
                const isActive = i === currentProject;
                return (
                  <li
                    onClick={() => scrollTo(i)}
                    key={p.name}
                    className={clsx(
                      styles.titleBlock__pad,
                      isActive && styles['titleBlock__pad--active']
                    )}
                  ></li>
                );
              })}
            </ul>
            <div className={styles.titleBlock__controlsArrows}>
              <CircleButton
                name="scroll to previous"
                as={'button'}
                content={<ArrowIcon />}
                theme="light"
                styleType="secondary"
                onClick={() => scrollPrev(largeDelay)}
              />
              <CircleButton
                name="scroll to next"
                as={'button'}
                content={<ArrowIcon isRight />}
                theme="light"
                onClick={() => scrollNext(largeDelay)}
                styleType="secondary"
              />
            </div>
          </div>
          <div className={styles.titleBlock__slideInfo}>
            <P2>{location}</P2>
            <VerticalLineSmall className={styles.titleBlock__verticalLine} />
            <P2>{projects[currentProject].name}</P2>
            <VerticalLineSmall className={styles.titleBlock__verticalLine} />
            <P2 className={styles.titleBlock__price}>
              {t('prepositions.from')} {cur}
              {money}
            </P2>
          </div>
          {!hideObjectNamesOnMobile && (
            <div
              className={clsx(
                styles.titleBlock__slideInfo,
                styles['titleBlock__slideInfo--mobile']
              )}
            >
              <span>
                <P2>{projects[currentProject].name}</P2>
                <VerticalLineSmall
                  className={styles.titleBlock__verticalLine}
                />
              </span>
              <P2>{location}</P2>
              <VerticalLineSmall className={styles.titleBlock__verticalLine} />
              <P2 className={styles.titleBlock__price}>
                {t('prepositions.from')} {cur}
                {money}
              </P2>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
